import { useCallback, useEffect, useRef, useState } from 'react';
import { getUserActions } from '../api';
import type { UserActionLogListingItem, UserActionsFilters } from '../types';

const DEFAULT_LIMIT = 20;
const DEFAULT_RANGE_DAYS = 7;
const SEARCH_DEBOUNCE_MS = 400;

function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}

export function getDefaultUserActionsFilters(): UserActionsFilters {
  const to = nowSec();
  const from = to - DEFAULT_RANGE_DAYS * 24 * 60 * 60;
  return {
    user_id: null,
    project_id: null,
    category: null,
    action: null,
    http_method: null,
    subject_type: null,
    subject_id: null,
    date_from: from,
    date_to: to,
    search: '',
    limit: DEFAULT_LIMIT,
    offset: 0,
  };
}

export interface UseUserActionsResult {
  filters: UserActionsFilters;
  setFilters: (updater: (prev: UserActionsFilters) => UserActionsFilters) => void;
  resetFilters: () => void;
  items: UserActionLogListingItem[];
  count: number;
  loading: boolean;
  error: string | null;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  page: number;
  reload: () => void;
}

export function useUserActions(): UseUserActionsResult {
  const [filters, setFiltersState] = useState<UserActionsFilters>(getDefaultUserActionsFilters);
  const [items, setItems] = useState<UserActionLogListingItem[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reqIdRef = useRef(0);

  const load = useCallback(async (f: UserActionsFilters) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await getUserActions(f);
      if (reqId !== reqIdRef.current) return;
      setItems(data.items);
      setCount(data.count);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      setError(e instanceof Error ? e.message : 'Ошибка загрузки журнала');
      setItems([]);
      setCount(0);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevSearchRef = useRef(filters.search);

  useEffect(() => {
    const searchChanged = prevSearchRef.current !== filters.search;
    prevSearchRef.current = filters.search;

    if (searchChanged) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        load(filters);
      }, SEARCH_DEBOUNCE_MS);
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }

    load(filters);
    return undefined;
  }, [filters, load]);

  const setFilters = useCallback((updater: (prev: UserActionsFilters) => UserActionsFilters) => {
    setFiltersState((prev) => {
      const next = updater(prev);
      const resetsOffset =
        next.user_id !== prev.user_id ||
        next.project_id !== prev.project_id ||
        next.category !== prev.category ||
        next.action !== prev.action ||
        next.http_method !== prev.http_method ||
        next.subject_type !== prev.subject_type ||
        next.subject_id !== prev.subject_id ||
        next.date_from !== prev.date_from ||
        next.date_to !== prev.date_to ||
        next.search !== prev.search ||
        next.limit !== prev.limit;
      return resetsOffset ? { ...next, offset: 0 } : next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(getDefaultUserActionsFilters());
  }, []);

  const setPage = useCallback((page: number) => {
    setFiltersState((prev) => ({ ...prev, offset: page * prev.limit }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setFiltersState((prev) => ({ ...prev, limit, offset: 0 }));
  }, []);

  const reload = useCallback(() => {
    load(filters);
  }, [filters, load]);

  const page = filters.limit > 0 ? Math.floor(filters.offset / filters.limit) : 0;

  return {
    filters,
    setFilters,
    resetFilters,
    items,
    count,
    loading,
    error,
    setPage,
    setLimit,
    page,
    reload,
  };
}
